import { Localized, useLocalization } from "@fluent/react"
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined"
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined"
import TitleOutlinedIcon from "@mui/icons-material/TitleOutlined"
import { Box, Chip, Paper, Stack, Typography } from "@mui/material"
import { StructureDefinitionDto } from "../structureSlice"

interface StructuresHeaderProps {
    structure: StructureDefinitionDto
}

export const StructuresHeader = ({ structure }: StructuresHeaderProps) => {
    const { l10n } = useLocalization()
 
    return (
        <Paper variant="outlined" sx={{ p: 0, overflow: "hidden" }}>
            <Box
                sx={{
                    bgcolor: theme => theme.palette.mode === "dark" ? "grey.900" : "grey.100",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    px: 1.25,
                    py: 1.25,
                }}
            >
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    alignItems={{ md: "center" }}
                    justifyContent="space-between"
                    spacing={1.5}
                    sx={{ minWidth: 0 }}
                >
                    <Stack spacing={1} sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Definicja struktury
                        </Typography>

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ minWidth: 0, flex: 1 }}>
                            <Stack sx={{ minWidth: 0, flex: { xs: "1 1 auto", sm: "1.2 1 0" } }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: ".02em" }}>
                                    {l10n.getString("structures-list-code", null, "Kod")}
                                </Typography>
                                <Box
                                    sx={{
                                        mt: 0.25,
                                        px: 1,
                                        py: 0.75,
                                        borderRadius: 1,
                                        bgcolor: "background.paper",
                                        border: "1px solid",
                                        borderColor: "divider",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        minWidth: 0,
                                    }}
                                    title={structure.code ?? ""}
                                >
                                    <LabelOutlinedIcon fontSize="small" color="action" />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 800,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            minWidth: 0,
                                        }}
                                    >
                                        {structure.code || "-"}
                                    </Typography>
                                </Box>
                            </Stack>

                            <Stack sx={{ minWidth: 0, flex: { xs: "1 1 auto", sm: "1 1 0" } }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: ".02em" }}>
                                    {l10n.getString("structures-list-name", null, "Nazwa")}
                                </Typography>
                                <Box
                                    sx={{
                                        mt: 0.25,
                                        px: 1,
                                        py: 0.75,
                                        borderRadius: 1,
                                        bgcolor: "background.paper",
                                        border: "1px solid",
                                        borderColor: "divider",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        minWidth: 0,
                                    }}
                                    title={structure.name ?? ""}
                                >
                                    <TitleOutlinedIcon fontSize="small" color="action" />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 800,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            minWidth: 0,
                                        }}
                                    >
                                        {structure.name || "-"}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Stack>
                    </Stack>

                    <Stack alignItems={{ xs: "flex-start", md: "flex-start" }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: ".02em" }}>
                            <Localized id="status">Status</Localized>
                        </Typography>
                        <Box sx={{ mt: 0.25 }}>
                            <Chip
                                label={structure.isActive ? l10n.getString("structures-status-active", null, "Aktywna") : l10n.getString("structures-status-inactive", null, "Nieaktywna")}
                                color={structure.isActive ? "success" : "default"}
                                variant={structure.isActive ? "filled" : "outlined"}
                                size="small"
                            />
                        </Box>
                    </Stack>
                </Stack>
            </Box>

            <Box sx={{ px: 1.25, py: 1.25 }}>
                <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: ".02em" }}>
                        {l10n.getString("structures-list-description", null, "Opis")}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
                            <DescriptionOutlinedIcon fontSize="small" sx={{ mt: 0.25 }} />
                            <Typography variant="body2" color="text.secondary" title={structure.description ?? undefined}>
                                {structure.description || "-"}
                            </Typography>
                    </Stack>
                </Stack>
            </Box>
        </Paper>
    )
}